import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { ArrowLeft, ChevronDown, ChevronRight, Copy, Loader2, MessageCircle } from "lucide-react";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

type FeedbackStatus = "new" | "needs_input" | "reviewing" | "resolved" | "wontfix";

type ClaudeTriage = {
  kind: "claude-triage";
  commitSha: string | null;
  commitUrl: string | null;
  commitMessage: string | null;
  summary: string;
  runUrl: string | null;
  at: string;
};

function parseTriage(adminNotes: string | null): ClaudeTriage | null {
  if (!adminNotes) return null;
  const trimmed = adminNotes.trim();
  if (!trimmed.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && parsed.kind === "claude-triage") return parsed as ClaudeTriage;
  } catch {
    /* not JSON — plain note */
  }
  return null;
}

type NavEntry = { path: string; at: number };
type TapEntry = { tag: string; text: string; x: number; y: number; at: number };
type ErrorEntry = { type: string; message: string; source?: string | null; stack?: string | null; at: number };

type FeedbackDiagnosticsBase = {
  screenshotUrls?: string[];
};

type FeedbackDiagnostics = FeedbackDiagnosticsBase & {
  platform?: string;
  device?: string;
  viewport?: { width?: number; height?: number };
  screen?: { width?: number; height?: number };
  devicePixelRatio?: number;
  orientation?: string | null;
  language?: string | null;
  online?: boolean;
  connection?: { type?: string | null; effectiveType?: string | null; downlink?: number | null; rtt?: number | null; saveData?: boolean } | null;
  memory?: { usedMB?: number; totalMB?: number; limitMB?: number } | null;
  sessionDurationSec?: number | null;
  serviceWorker?: string;
  storage?: { usedMB?: number; quotaMB?: number } | null;
  localStorageKeys?: number;
  sessionStorageKeys?: number;
  capturedAt?: string;
  navTrail?: NavEntry[];
  tapTrail?: TapEntry[];
  errorTrail?: ErrorEntry[];
} | null;

type FeedbackRow = {
  id: string;
  userId: string | null;
  email: string | null;
  category: "bug" | "idea" | "praise" | "other";
  message: string;
  pageUrl: string | null;
  userAgent: string | null;
  appVersion: string | null;
  diagnostics: FeedbackDiagnostics;
  status: FeedbackStatus;
  adminNotes: string | null;
  theme: string | null;
  createdAt: string | null;
  userEmail: string | null;
  userName: string | null;
};

function formatClockTime(ms: number) {
  try {
    return new Date(ms).toLocaleTimeString(undefined, { hour12: false });
  } catch {
    return "";
  }
}

function formatDiagnosticsSummary(diagnostics: FeedbackDiagnostics): string | null {
  if (!diagnostics) return null;
  const bits: string[] = [];
  if (diagnostics.device) bits.push(diagnostics.device);
  else if (diagnostics.platform) bits.push(diagnostics.platform);
  const vw = diagnostics.viewport?.width;
  const vh = diagnostics.viewport?.height;
  if (vw && vh) bits.push(`${vw}×${vh}`);
  if (diagnostics.devicePixelRatio) bits.push(`${diagnostics.devicePixelRatio}x`);
  if (diagnostics.orientation) bits.push(diagnostics.orientation.replace(/-primary$/, ""));
  return bits.length ? bits.join(" · ") : null;
}

const STATUS_TABS: { value: FeedbackStatus | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "needs_input", label: "Needs you" },
  { value: "reviewing", label: "Reviewing" },
  { value: "resolved", label: "Resolved" },
  { value: "wontfix", label: "Won't fix" },
];

const CATEGORY_LABEL: Record<FeedbackRow["category"], string> = {
  bug: "Bug",
  idea: "Idea",
  praise: "Praise",
  other: "Other",
};

// Theme regexes live in shared/ so the admin UI and the server insert
// hook stay aligned. For new rows the theme is persisted on insert
// (row.theme); for older rows we fall back to the regex client-side.
import { themeForMessage } from "@shared/feedback-themes";

function themeFor(row: { theme?: string | null; message?: string | null }): string {
  if (row.theme && row.theme.trim()) return row.theme;
  return themeForMessage(row.message ?? "");
}

function formatTimestamp(iso: string | null) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function reporterLabel(row: FeedbackRow) {
  if (row.userName) return row.userName;
  if (row.userEmail) return row.userEmail;
  if (row.email) return row.email;
  return "anonymous";
}

function buildClaudeDigest(rows: FeedbackRow[]): string {
  const now = new Date().toISOString().replace("T", " ").slice(0, 16);
  const header = `# Stoop Beta Feedback Digest\n# ${rows.length} item${rows.length === 1 ? "" : "s"}, generated ${now}\n\n`;
  const items = rows
    .map((r, i) => {
      const who = reporterLabel(r);
      const when = formatTimestamp(r.createdAt);
      const page = r.pageUrl ? ` — ${r.pageUrl}` : "";
      const notes = r.adminNotes ? `\n\n_Admin note:_ ${r.adminNotes}` : "";
      const envLines: string[] = [];
      if (r.diagnostics) {
        const d = r.diagnostics;
        const device = [
          d.device || d.platform,
          d.viewport && `viewport ${d.viewport.width}×${d.viewport.height}`,
          d.screen && `screen ${d.screen.width}×${d.screen.height}`,
          d.devicePixelRatio && `DPR ${d.devicePixelRatio}`,
          d.orientation,
          d.language,
        ]
          .filter(Boolean)
          .join(" · ");
        if (device) envLines.push(`_Device:_ ${device}`);
        if (d.navTrail && d.navTrail.length > 0) {
          const trail = d.navTrail
            .map((entry) => `  - ${formatClockTime(entry.at)} → ${entry.path}`)
            .join("\n");
          envLines.push(`_Nav trail (last ${d.navTrail.length}):_\n${trail}`);
        }
        if (d.tapTrail && d.tapTrail.length > 0) {
          const taps = d.tapTrail
            .map((t) => `  - ${formatClockTime(t.at)} tap <${t.tag}> "${t.text}" at (${t.x},${t.y})`)
            .join("\n");
          envLines.push(`_Tap trail (last ${d.tapTrail.length}):_\n${taps}`);
        }
        if (d.errorTrail && d.errorTrail.length > 0) {
          const errors = d.errorTrail
            .map((e) => {
              const head = `  - ${formatClockTime(e.at)} [${e.type}] ${e.message}${e.source ? ` @ ${e.source}` : ""}`;
              if (!e.stack) return head;
              const stackLines = e.stack.split("\n").slice(0, 5).map((l: string) => `      ${l.trim()}`).join("\n");
              return `${head}\n${stackLines}`;
            })
            .join("\n");
          envLines.push(`_Errors (${d.errorTrail.length}):_\n${errors}`);
        }
        const extras: string[] = [];
        if (d.online === false) extras.push("**OFFLINE**");
        if (d.connection?.effectiveType) extras.push(`net: ${d.connection.effectiveType}${d.connection.rtt ? ` ${d.connection.rtt}ms` : ""}`);
        if (d.memory) extras.push(`mem: ${d.memory.usedMB}/${d.memory.totalMB}MB`);
        if (d.sessionDurationSec != null) extras.push(`session: ${Math.floor(d.sessionDurationSec / 60)}m${d.sessionDurationSec % 60}s`);
        if (d.serviceWorker && d.serviceWorker !== "unsupported") extras.push(`SW: ${d.serviceWorker}`);
        if (d.storage) extras.push(`storage: ${d.storage.usedMB ?? "?"}/${d.storage.quotaMB ?? "?"}MB`);
        if (d.localStorageKeys || d.sessionStorageKeys) extras.push(`keys: ls:${d.localStorageKeys ?? 0} ss:${d.sessionStorageKeys ?? 0}`);
        if (extras.length) envLines.push(`_Context:_ ${extras.join(" · ")}`);
      }
      if (r.userAgent) envLines.push(`_User-Agent:_ ${r.userAgent}`);
      const env = envLines.length ? `\n\n${envLines.join("\n\n")}` : "";
      return `---\n## #${i + 1} [${CATEGORY_LABEL[r.category].toUpperCase()}] from ${who} on ${when}${page}\n\n${r.message}${notes}${env}\n`;
    })
    .join("\n");
  return header + items + "\n";
}

export default function AdminFeedback() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | "all">("all");
  // Render in pages — there are hundreds of reports and rendering them all at
  // once stalled the main thread for 5-13s on mobile (ANR). Surfaced by the
  // freeze detector in errorTrail.ts.
  const PAGE_SIZE = 25;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [themeFilter, setThemeFilter] = useState<string>("all");
  const [testerFilter, setTesterFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const isDemoAdmin = Boolean((user as any)?.isDemoAdmin);

  const { data: rawRows = [], isLoading } = useQuery<FeedbackRow[]>({
    queryKey: ["/api/admin/feedback", statusFilter],
    queryFn: async () => {
      const qs = statusFilter === "all" ? "" : `?status=${statusFilter}`;
      const res = await fetch(`/api/admin/feedback${qs}`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: isDemoAdmin,
  });

  // Compute theme/tester counts off the raw (status-filtered) set so the
  // chip counts always match what's actually selectable. Apply theme +
  // tester + search filters in-memory.
  const rowsWithTheme = useMemo(
    () => rawRows.map((r) => ({ row: r, theme: themeFor(r) })),
    [rawRows],
  );

  const themeCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const { theme } of rowsWithTheme) m.set(theme, (m.get(theme) ?? 0) + 1);
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [rowsWithTheme]);

  const testerCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const { row } of rowsWithTheme) {
      const e = String(row.email || "anonymous").trim().toLowerCase();
      m.set(e, (m.get(e) ?? 0) + 1);
    }
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [rowsWithTheme]);

  const rows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rowsWithTheme
      .filter(({ row, theme }) => {
        if (themeFilter !== "all" && theme !== themeFilter) return false;
        if (testerFilter !== "all") {
          const e = String(row.email || "anonymous").trim().toLowerCase();
          if (e !== testerFilter) return false;
        }
        if (q && !String(row.message || "").toLowerCase().includes(q)) return false;
        return true;
      })
      .map(({ row }) => row);
  }, [rowsWithTheme, themeFilter, testerFilter, searchQuery]);

  const updateMutation = useMutation({
    mutationFn: async (input: { id: string; status: FeedbackStatus; adminNotes: string | null }) => {
      const res = await fetch(`/api/admin/feedback/${input.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: input.status, adminNotes: input.adminNotes }),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/feedback"] });
    },
    onError: (err: any) => {
      toast({ title: "Update failed", description: err?.message || "Try again.", variant: "destructive" });
    },
  });

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: rows.length };
    for (const r of rows) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [rows]);

  const copyDigest = async () => {
    const digest = buildClaudeDigest(rows);
    try {
      await navigator.clipboard.writeText(digest);
      toast({ title: "Copied to clipboard", description: `${rows.length} item${rows.length === 1 ? "" : "s"} ready to paste.` });
    } catch {
      toast({ title: "Copy failed", description: "Select the text and copy manually.", variant: "destructive" });
    }
  };

  // Group resolved feedback by tester email so the operator can fire off
  // a "here's what I fixed for you" recap to each beta tester without
  // retyping. Only renders when there's at least one tester with a
  // resolved item.
  const resolvedByTester = useMemo(() => {
    const map = new Map<string, { count: number; latestResolvedAt: number }>();
    for (const r of rows) {
      if (r.status !== "resolved") continue;
      const email = String((r as any).email || "").trim().toLowerCase();
      if (!email) continue;
      const resolvedAt = r.createdAt ? new Date(r.createdAt).getTime() : 0;
      const cur = map.get(email);
      if (cur) {
        cur.count += 1;
        if (resolvedAt > cur.latestResolvedAt) cur.latestResolvedAt = resolvedAt;
      } else {
        map.set(email, { count: 1, latestResolvedAt: resolvedAt });
      }
    }
    return Array.from(map.entries())
      .map(([email, v]) => ({ email, ...v }))
      .sort((a, b) => b.count - a.count);
  }, [rows]);

  // localStorage-backed "last sent" timestamps per tester. Lives on this
  // device only — Mark's the sole admin, no cross-device sync needed.
  // We re-render on send so the indicator updates immediately.
  const SENT_KEY_PREFIX = "marketlink.recap-sent.";
  const [sentTick, setSentTick] = useState(0);
  const sentAtFor = (email: string): number | null => {
    if (typeof window === "undefined") return null;
    try {
      const v = window.localStorage.getItem(SENT_KEY_PREFIX + email);
      return v ? Number(v) || null : null;
    } catch {
      return null;
    }
  };
  const formatSinceSent = (ms: number): string => {
    const delta = Date.now() - ms;
    if (delta < 60_000) return "just now";
    if (delta < 3_600_000) return `${Math.round(delta / 60_000)}m ago`;
    if (delta < 86_400_000) return `${Math.round(delta / 3_600_000)}h ago`;
    return `${Math.round(delta / 86_400_000)}d ago`;
  };

  const copyTesterReport = async (email: string) => {
    try {
      const res = await fetch(`/api/admin/feedback/report?email=${encodeURIComponent(email)}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error((await res.text()) || "Failed to build report");
      const { report, count } = (await res.json()) as { report: string; count: number };
      if (!report) {
        toast({ title: "No resolved fixes for this tester yet" });
        return;
      }
      await navigator.clipboard.writeText(report);
      try {
        window.localStorage.setItem(SENT_KEY_PREFIX + email, String(Date.now()));
      } catch { /* quota */ }
      setSentTick((n) => n + 1);
      toast({
        title: `Copied report for ${email}`,
        description: `${count} fix${count === 1 ? "" : "es"} — paste into Messages.`,
      });
    } catch (err: any) {
      toast({
        title: "Couldn't copy report",
        description: err?.message || "Try again.",
        variant: "destructive",
      });
    }
  };

  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isDemoAdmin) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <div className="container mx-auto max-w-xl px-4 py-10 text-center">
          <h1 className="text-xl font-semibold">Admin only</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            This page is restricted to the demo admin.
          </p>
          <Button asChild variant="outline" className="mt-4 rounded-full">
            <Link href="/">Back to home</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="container mx-auto max-w-3xl px-4 py-6 overflow-x-hidden">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm" className="gap-1">
              <Link href="/"><ArrowLeft className="h-4 w-4" /> Home</Link>
            </Button>
          </div>
          <Button type="button" size="sm" onClick={copyDigest} disabled={rows.length === 0}>
            <Copy className="mr-2 h-4 w-4" />
            Copy {statusFilter === "all" ? "all" : statusFilter} for Claude
          </Button>
        </div>

        <div className="flex items-baseline gap-3 flex-wrap">
          <h1 className="text-2xl font-bold tracking-tight">Beta feedback</h1>
          <Link href="/admin/triage" className="text-sm text-blue-600 hover:underline">
            Triage runs →
          </Link>
          <Link href="/admin/messages" className="text-sm text-blue-600 hover:underline">
            Support messages →
          </Link>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          All notes submitted via the floating feedback button. Tap <strong>Copy for Claude</strong> to paste the visible list into a new chat.
        </p>

        {resolvedByTester.length > 0 && (
          <div className="mt-4 rounded-lg border border-dashed bg-muted/30 p-3 space-y-2">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Send tester their fix recap
            </div>
            <div className="flex flex-wrap gap-1.5">
              {resolvedByTester.map(({ email, count, latestResolvedAt }) => {
                const sentAt = sentAtFor(email);
                // Stale signal: there are resolutions newer than the last send,
                // OR they've crossed the 3-fix threshold and we've never sent.
                const hasNewSinceSent = sentAt == null
                  ? count >= 3
                  : latestResolvedAt > sentAt;
                return (
                  <Button
                    key={`${email}-${sentTick}`}
                    type="button"
                    size="sm"
                    variant={hasNewSinceSent ? "default" : "outline"}
                    onClick={() => copyTesterReport(email)}
                    className="text-xs"
                  >
                    <Copy className="mr-1.5 h-3 w-3" />
                    {email}
                    <span className="ml-1.5 rounded-full bg-background/40 px-1.5 py-0.5 text-[10px] font-semibold">
                      {count}
                    </span>
                    {sentAt != null && (
                      <span className="ml-1.5 text-[10px] opacity-80">
                        · sent {formatSinceSent(sentAt)}
                      </span>
                    )}
                  </Button>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Tap a name to copy a per-fix recap (summary + smoke test + force-close reminder). Filled buttons mean there are new fixes since you last sent (or 3+ resolved if never sent).
            </p>
          </div>
        )}

        <div className="mt-4 space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setStatusFilter(tab.value)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                  statusFilter === tab.value
                    ? "bg-primary text-primary-foreground"
                    : "border border-border text-foreground hover:bg-muted"
                }`}
              >
                {tab.label}
                {counts[tab.value] !== undefined && (
                  <span className="ml-1 text-[10px] opacity-70">{counts[tab.value]}</span>
                )}
              </button>
            ))}
          </div>

          <input
            type="search"
            placeholder="Search message text…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs"
          />

          {themeCounts.length > 1 && (
            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                onClick={() => setThemeFilter("all")}
                className={`rounded-full px-2 py-0.5 text-[10px] font-medium transition ${
                  themeFilter === "all"
                    ? "bg-foreground text-background"
                    : "border border-border text-muted-foreground hover:bg-muted"
                }`}
              >
                All themes
              </button>
              {themeCounts.map(([theme, n]) => (
                <button
                  key={theme}
                  type="button"
                  onClick={() => setThemeFilter(theme === themeFilter ? "all" : theme)}
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium transition ${
                    themeFilter === theme
                      ? "bg-foreground text-background"
                      : "border border-border text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {theme}
                  <span className="ml-1 opacity-70">{n}</span>
                </button>
              ))}
            </div>
          )}

          {testerCounts.length > 1 && (
            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                onClick={() => setTesterFilter("all")}
                className={`rounded-full px-2 py-0.5 text-[10px] font-medium transition ${
                  testerFilter === "all"
                    ? "bg-foreground text-background"
                    : "border border-border text-muted-foreground hover:bg-muted"
                }`}
              >
                All testers
              </button>
              {testerCounts.map(([tester, n]) => (
                <button
                  key={tester}
                  type="button"
                  onClick={() => setTesterFilter(tester === testerFilter ? "all" : tester)}
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium transition ${
                    testerFilter === tester
                      ? "bg-foreground text-background"
                      : "border border-border text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {tester.split("@")[0]}
                  <span className="ml-1 opacity-70">{n}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-5 space-y-3">
          {isLoading && (
            <div className="flex items-center gap-2 py-8 text-muted-foreground justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading feedback…
            </div>
          )}

          {!isLoading && rows.length === 0 && (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No feedback yet{statusFilter !== "all" ? ` in "${statusFilter}"` : ""}.
            </div>
          )}

          {!isLoading && rows.slice(0, visibleCount).map((row) => (
            <FeedbackCard
              key={row.id}
              row={row}
              onUpdate={(status, adminNotes) => updateMutation.mutate({ id: row.id, status, adminNotes })}
              isUpdating={updateMutation.isPending && updateMutation.variables?.id === row.id}
            />
          ))}

          {!isLoading && rows.length > visibleCount && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
            >
              Show more ({rows.length - visibleCount} older)
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function FeedbackCard({
  row,
  onUpdate,
  isUpdating,
}: {
  row: FeedbackRow;
  onUpdate: (status: FeedbackStatus, adminNotes: string | null) => void;
  isUpdating: boolean;
}) {
  const triage = parseTriage(row.adminNotes);
  const [notesDraft, setNotesDraft] = useState(triage ? "" : (row.adminNotes ?? ""));
  const [notesOpen, setNotesOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const diagnosticsSummary = formatDiagnosticsSummary(row.diagnostics);
  const navTrail = row.diagnostics?.navTrail ?? [];
  const tapTrail = row.diagnostics?.tapTrail ?? [];
  const errorTrail = row.diagnostics?.errorTrail ?? [];
  const screenshotUrls = row.diagnostics?.screenshotUrls ?? [];
  const hasErrors = errorTrail.length > 0;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide">
            {CATEGORY_LABEL[row.category]}
          </span>
          <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-medium text-foreground">
            {themeFor(row)}
          </span>
          {row.status === "needs_input" && (
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
              Needs your input
            </span>
          )}
          <span>{reporterLabel(row)}</span>
          <span>·</span>
          <span>{formatTimestamp(row.createdAt)}</span>
          {row.pageUrl && (
            <>
              <span>·</span>
              <span className="truncate max-w-[50%]">{row.pageUrl}</span>
            </>
          )}
        </div>

        <p className="mt-2 whitespace-pre-wrap break-words text-sm">{row.message}</p>

        {(row.userId || row.email) && (
          <Link
            href={
              row.userId
                ? `/admin/messages?user=${encodeURIComponent(row.userId)}${row.userName ? `&name=${encodeURIComponent(row.userName)}` : ""}`
                : `/admin/messages?email=${encodeURIComponent(row.email!)}${row.userName ? `&name=${encodeURIComponent(row.userName)}` : ""}`
            }
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
          >
            <MessageCircle className="h-3.5 w-3.5" /> Message this user
          </Link>
        )}

        {(diagnosticsSummary || navTrail.length > 0 || tapTrail.length > 0 || hasErrors) && (
          <div className="mt-3 rounded-md border border-border bg-muted/40">
            <button
              type="button"
              onClick={() => setDetailsOpen((v) => !v)}
              className="flex w-full items-center gap-2 px-3 py-2 text-[11px] text-muted-foreground"
            >
              {detailsOpen ? (
                <ChevronDown className="h-3.5 w-3.5 shrink-0" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 shrink-0" />
              )}
              <span className="truncate">
                {diagnosticsSummary ?? "Session details"}
                {(navTrail.length > 0 || tapTrail.length > 0 || hasErrors) && (
                  <span className="ml-2 text-[10px] opacity-70">
                    ({navTrail.length} step{navTrail.length === 1 ? "" : "s"}{tapTrail.length > 0 ? `, ${tapTrail.length} tap${tapTrail.length === 1 ? "" : "s"}` : ""}{hasErrors ? `, ${errorTrail.length} error${errorTrail.length === 1 ? "" : "s"}` : ""})
                  </span>
                )}
              </span>
            </button>
            {detailsOpen && (
              <div className="space-y-3 border-t border-border px-3 py-2 text-[11px]">
                {row.diagnostics && (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-[10px]">
                    {row.diagnostics.device && <div className="col-span-2"><span className="text-muted-foreground">device </span>{row.diagnostics.device}</div>}
                    {!row.diagnostics.device && row.diagnostics.platform && <div><span className="text-muted-foreground">platform </span>{row.diagnostics.platform}</div>}
                    {row.diagnostics.viewport && (
                      <div>
                        <span className="text-muted-foreground">viewport </span>
                        {row.diagnostics.viewport.width}×{row.diagnostics.viewport.height}
                      </div>
                    )}
                    {row.diagnostics.screen && (
                      <div>
                        <span className="text-muted-foreground">screen </span>
                        {row.diagnostics.screen.width}×{row.diagnostics.screen.height}
                      </div>
                    )}
                    {row.diagnostics.devicePixelRatio && (
                      <div><span className="text-muted-foreground">DPR </span>{row.diagnostics.devicePixelRatio}</div>
                    )}
                    {row.diagnostics.orientation && (
                      <div><span className="text-muted-foreground">orient </span>{row.diagnostics.orientation}</div>
                    )}
                    {row.diagnostics.language && (
                      <div><span className="text-muted-foreground">lang </span>{row.diagnostics.language}</div>
                    )}
                    <div><span className="text-muted-foreground">online </span>{row.diagnostics.online === false ? "❌ offline" : "✓"}</div>
                    {row.diagnostics.connection?.effectiveType && (
                      <div><span className="text-muted-foreground">net </span>{row.diagnostics.connection.effectiveType}{row.diagnostics.connection.rtt ? ` ${row.diagnostics.connection.rtt}ms` : ""}</div>
                    )}
                    {row.diagnostics.memory && (
                      <div><span className="text-muted-foreground">mem </span>{row.diagnostics.memory.usedMB}/{row.diagnostics.memory.totalMB} MB</div>
                    )}
                    {row.diagnostics.sessionDurationSec != null && (
                      <div><span className="text-muted-foreground">session </span>{Math.floor(row.diagnostics.sessionDurationSec / 60)}m {row.diagnostics.sessionDurationSec % 60}s</div>
                    )}
                    {row.diagnostics.serviceWorker && row.diagnostics.serviceWorker !== "unsupported" && (
                      <div><span className="text-muted-foreground">SW </span>{row.diagnostics.serviceWorker}</div>
                    )}
                    {row.diagnostics.storage && (
                      <div><span className="text-muted-foreground">storage </span>{row.diagnostics.storage.usedMB ?? "?"}MB / {row.diagnostics.storage.quotaMB ?? "?"}MB</div>
                    )}
                    {(row.diagnostics.localStorageKeys != null || row.diagnostics.sessionStorageKeys != null) && (
                      <div><span className="text-muted-foreground">keys </span>ls:{row.diagnostics.localStorageKeys ?? 0} ss:{row.diagnostics.sessionStorageKeys ?? 0}</div>
                    )}
                  </div>
                )}
                {navTrail.length > 0 && (
                  <div>
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Nav trail (last {navTrail.length})
                    </p>
                    <ol className="space-y-0.5 font-mono text-[10px]">
                      {navTrail.map((entry, idx) => (
                        <li key={idx} className="truncate">
                          <span className="text-muted-foreground">{formatClockTime(entry.at)}</span>
                          <span className="mx-1">→</span>
                          {entry.path}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                {tapTrail.length > 0 && (
                  <div>
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Tap trail (last {tapTrail.length})
                    </p>
                    <ol className="space-y-0.5 font-mono text-[10px]">
                      {tapTrail.map((entry, idx) => (
                        <li key={idx} className="truncate">
                          <span className="text-muted-foreground">{formatClockTime(entry.at)}</span>
                          <span className="mx-1">→</span>
                          <span className="text-primary">&lt;{entry.tag}&gt;</span>
                          {entry.text && <span className="ml-1">"{entry.text}"</span>}
                          <span className="ml-1 text-muted-foreground">({entry.x},{entry.y})</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                {errorTrail.length > 0 && (
                  <div>
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-red-500">
                      Errors ({errorTrail.length})
                    </p>
                    <ol className="space-y-1 font-mono text-[10px]">
                      {errorTrail.map((entry, idx) => (
                        <li key={idx}>
                          <div className="truncate">
                            <span className="text-muted-foreground">{formatClockTime(entry.at)}</span>
                            <span className="mx-1 text-red-400">[{entry.type}]</span>
                            <span>{entry.message}</span>
                            {entry.source && <span className="ml-1 text-muted-foreground">@ {entry.source}</span>}
                          </div>
                          {entry.stack && (
                            <pre className="mt-0.5 whitespace-pre-wrap break-all pl-4 text-[9px] text-muted-foreground">
                              {entry.stack.split("\n").slice(0, 5).join("\n")}
                            </pre>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                {row.userAgent && (
                  <div>
                    <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      User-Agent
                    </p>
                    <p className="break-all font-mono text-[10px]">{row.userAgent}</p>
                  </div>
                )}
                {screenshotUrls.length > 0 && (
                  <div>
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Screenshots ({screenshotUrls.length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {screenshotUrls.map((url, idx) => (
                        <a
                          key={url}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={url}
                            alt={`Screenshot ${idx + 1}`}
                            className="h-24 w-24 rounded-md border object-cover"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {triage && (
          <div className="mt-3 rounded-lg border border-green-500/30 bg-green-500/5 p-3">
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-green-700 dark:text-green-400">
                Claude triage
              </span>
              <span className="text-[10px] text-muted-foreground">
                {new Date(triage.at).toLocaleString()}
              </span>
            </div>
            <p className="whitespace-pre-wrap break-words text-sm">{triage.summary}</p>
            <div className="mt-2 flex flex-wrap gap-3 text-[11px]">
              {triage.commitUrl && triage.commitSha && (
                <a
                  href={triage.commitUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-primary hover:underline"
                >
                  {triage.commitSha.slice(0, 7)} →
                </a>
              )}
              {triage.runUrl && (
                <a
                  href={triage.runUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  workflow run
                </a>
              )}
            </div>
          </div>
        )}

        {((row.adminNotes && !triage) || notesOpen) && (
          <div className="mt-3">
            <Textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              placeholder="Admin notes (internal)"
              className="min-h-[60px] text-sm"
              maxLength={5000}
            />
          </div>
        )}

        <FeedbackThread
          feedbackId={row.id}
          highlight={row.status === "needs_input"}
          // The commit subject is the one line of a triage result that reads as
          // plain English to the person who reported it.
          fixSummary={triage?.commitMessage?.split("\n")[0]?.trim() || null}
        />

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1">
            {(["new", "needs_input", "reviewing", "resolved", "wontfix"] as FeedbackStatus[]).map((s) => (
              <button
                key={s}
                type="button"
                disabled={isUpdating}
                onClick={() => onUpdate(s, notesDraft.trim() || null)}
                className={`rounded-full px-2.5 py-0.5 text-[10px] font-medium capitalize transition ${
                  row.status === s
                    ? "bg-primary text-primary-foreground"
                    : "border border-border text-foreground hover:bg-muted"
                }`}
              >
                {s === "wontfix" ? "Won't fix" : s}
              </button>
            ))}
          </div>
          {(!row.adminNotes || triage) && !notesOpen && (
            <button
              type="button"
              onClick={() => setNotesOpen(true)}
              className="text-[11px] text-primary hover:underline"
            >
              {triage ? "Add manual note" : "Add note"}
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

type ThreadMessage = {
  id: string;
  authorRole: "agent" | "admin" | "reporter";
  body: string;
  createdAt: string | null;
};

function FeedbackThread({
  feedbackId,
  highlight,
  fixSummary,
}: {
  feedbackId: string;
  highlight: boolean;
  fixSummary?: string | null;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(highlight);
  const [draft, setDraft] = useState("");

  const { data } = useQuery<{ messages: ThreadMessage[] }>({
    queryKey: ["/api/feedback", feedbackId, "thread"],
    queryFn: async () => {
      const res = await fetch(`/api/feedback/${feedbackId}/thread`, { credentials: "include" });
      if (!res.ok) throw new Error("thread load failed");
      return res.json();
    },
    enabled: open,
    refetchInterval: open ? 20_000 : false,
  });

  const messages = data?.messages ?? [];

  const reply = useMutation({
    mutationFn: async ({ body, toReporter }: { body: string; toReporter: boolean }) => {
      const res = await fetch(`/api/feedback/${feedbackId}/thread`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body, toReporter }),
      });
      if (!res.ok) throw new Error(await res.text().catch(() => "reply failed"));
      return res.json() as Promise<{ requeued: boolean; notified?: boolean }>;
    },
    onSuccess: (data) => {
      setDraft("");
      queryClient.invalidateQueries({ queryKey: ["/api/feedback", feedbackId, "thread"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/feedback"] });
      toast({
        title: data.requeued
          ? "Sent — re-running triage with your answer"
          : data.notified
            ? "Sent to the reporter"
            : "Posted, but the reporter has no matching account — they won't be notified",
      });
    },
    onError: (e: any) => {
      toast({ title: "Couldn't send", description: e?.message || "Try again", variant: "destructive" });
    },
  });

  return (
    <div className={`mt-3 rounded-lg border p-3 ${highlight ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-muted/30"}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        Triage chat
        {highlight && <span className="text-amber-600 dark:text-amber-400">· agent is waiting on you</span>}
      </button>

      {open && (
        <div className="mt-2 space-y-2">
          {messages.length === 0 ? (
            <p className="text-xs text-muted-foreground">No messages yet.</p>
          ) : (
            messages.map((m) => (
              <div
                key={m.id}
                className={`rounded-md px-2.5 py-1.5 text-sm ${m.authorRole === "agent" ? "bg-background border border-border" : "bg-primary/10"}`}
              >
                <div className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  {m.authorRole === "agent" ? "Triage agent" : m.authorRole === "admin" ? "You" : "Reporter"}
                  {m.createdAt ? ` · ${new Date(m.createdAt).toLocaleString()}` : ""}
                </div>
                <p className="whitespace-pre-wrap break-words">{m.body}</p>
              </div>
            ))
          )}

          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Answer the agent, or write a reply for the person who reported this."
            className="min-h-[60px] text-sm"
            maxLength={8000}
          />
          <div className="flex flex-wrap items-center justify-end gap-2">
            {fixSummary && (
              <button
                type="button"
                onClick={() =>
                  setDraft(
                    `Thanks for flagging this — it's fixed and live. ${fixSummary}. Close and reopen Stoop if you still see it, and reply here if it isn't sorted.`,
                  )
                }
                className="mr-auto text-xs text-primary hover:underline"
              >
                Draft a reply from the fix
              </button>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={reply.isPending}
              onClick={() => {
                const body = draft.trim();
                if (!body) {
                  toast({ title: "Write a reply first" });
                  return;
                }
                reply.mutate({ body, toReporter: true });
              }}
            >
              {reply.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Send to reporter
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={reply.isPending}
              onClick={() => {
                const body = draft.trim();
                if (!body) {
                  toast({ title: "Write a reply first" });
                  return;
                }
                reply.mutate({ body, toReporter: false });
              }}
            >
              {reply.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Send & re-run triage
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
